import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, finalize, map, take } from 'rxjs';
import {
    AdminCity,
    AdminCityUserAccessRow,
    AdminDashboardMetrics,
    AdminUpdateUserRequest,
    AdminUser,
    AdminUserCityAccessPayload
} from './admin-dashboard.models';
import { AdminDashboardService } from './admin-dashboard.service';

@Injectable({ providedIn: 'root' })
export class AdminDashboardStore {
    private readonly adminDashboardService = inject(AdminDashboardService);

    private readonly citiesSubject = new BehaviorSubject<AdminCity[]>([]);
    private readonly usersSubject = new BehaviorSubject<AdminUser[]>([]);
    private readonly selectedCitySubject = new BehaviorSubject<AdminCity | null>(null);
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);

    readonly cities$ = this.citiesSubject.asObservable();
    readonly users$ = this.usersSubject.asObservable();
    readonly loading$ = this.loadingSubject.asObservable();
    readonly selectedCity$ = this.selectedCitySubject.asObservable();

    readonly metrics$ = combineLatest([this.cities$, this.users$]).pipe(
        map(([cities, users]) => this.buildMetrics(cities, users))
    );

    readonly selectedCityUsers$ = combineLatest([this.selectedCity$, this.users$]).pipe(
        map(([selectedCity, users]) => {
            if (!selectedCity) {
                return [];
            }

            return users
                .map<AdminCityUserAccessRow>((user) => {
                    const permission =
                        user.permissions.find((entry) => entry.cityId === selectedCity.id) ??
                        createEmptyPermission(selectedCity.id);

                    return {
                        userId: user.email,
                        name: user.name,
                        email: user.email,
                        status: user.status,
                        hasCityAccess:
                            permission.allAccess ||
                            permission.t0Access ||
                            permission.t3Access ||
                            permission.t6Access ||
                            permission.dataUploadAccess,
                        permission
                    };
                })
                .sort(
                    (left, right) =>
                        Number(right.hasCityAccess) - Number(left.hasCityAccess) ||
                        left.name.localeCompare(right.name)
                );
        })
    );

    load(): void {
        const selectedCityId = this.selectedCitySubject.value?.id ?? null;

        this.loadingSubject.next(true);

        this.adminDashboardService
            .getDashboardData()
            .pipe(
                take(1),
                finalize(() => this.loadingSubject.next(false))
            )
            .subscribe((data) => {
                this.citiesSubject.next(data.cities);
                this.usersSubject.next(data.users);

                if (selectedCityId) {
                    this.selectCity(selectedCityId);
                    return;
                }

                this.selectedCitySubject.next(null);
            });
    }

    selectCity(cityId: string): void {
        const city = this.citiesSubject.value.find((item) => item.id === cityId) ?? null;
        this.selectedCitySubject.next(city);
    }

    updateCityPermission(
        cityId: string,
        userId: string,
        patch: Partial<AdminUserCityAccessPayload>
    ): void {
        const user = this.usersSubject.value.find((item) => item.email === userId || item.id === userId);

        if (!user) {
            return;
        }

        const nextPermissions = this.applyPermissionPatch(user.permissions, cityId, patch);
        const payload: AdminUpdateUserRequest = {
            action: 'update',
            fullName: user.name,
            email: user.email,
            status: user.status,
            adminAccess: user.adminAccess,
            allAccess: false,
            cityAccess: nextPermissions
        };

        this.adminDashboardService
            .updateUser(user.email, payload)
            .pipe(take(1))
            .subscribe(() => this.load());
    }

    private applyPermissionPatch(
        permissions: AdminUserCityAccessPayload[],
        cityId: string,
        patch: Partial<AdminUserCityAccessPayload>
    ): AdminUserCityAccessPayload[] {
        const next = permissions.map((permission) => ({ ...permission }));
        const existing = next.find((permission) => permission.cityId === cityId);

        if (!existing) {
            const created: AdminUserCityAccessPayload = {
                cityId,
                allAccess: !!patch.allAccess,
                t0Access: !!patch.allAccess || !!patch.t0Access,
                t3Access: !!patch.allAccess || !!patch.t3Access,
                t6Access: !!patch.allAccess || !!patch.t6Access,
                dataUploadAccess: !!patch.allAccess || !!patch.dataUploadAccess
            };

            created.allAccess =
                created.t0Access &&
                created.t3Access &&
                created.t6Access &&
                created.dataUploadAccess;

            return created.allAccess ||
                created.t0Access ||
                created.t3Access ||
                created.t6Access ||
                created.dataUploadAccess
                ? [...next, created]
                : next;
        }

        if (patch.allAccess === true) {
            existing.allAccess = true;
            existing.t0Access = true;
            existing.t3Access = true;
            existing.t6Access = true;
            existing.dataUploadAccess = true;
        } else {
            if (patch.t0Access !== undefined) {
                existing.t0Access = patch.t0Access;
            }

            if (patch.t3Access !== undefined) {
                existing.t3Access = patch.t3Access;
            }

            if (patch.t6Access !== undefined) {
                existing.t6Access = patch.t6Access;
            }

            if (patch.dataUploadAccess !== undefined) {
                existing.dataUploadAccess = patch.dataUploadAccess;
            }

            existing.allAccess =
                existing.t0Access &&
                existing.t3Access &&
                existing.t6Access &&
                existing.dataUploadAccess;
        }

        return next.filter(
            (permission) =>
                permission.allAccess ||
                permission.t0Access ||
                permission.t3Access ||
                permission.t6Access ||
                permission.dataUploadAccess
        );
    }

    private buildMetrics(cities: AdminCity[], users: AdminUser[]): AdminDashboardMetrics {
        const activeUsers = users.filter((user) => user.status === 'active').length;
        const inactiveUsers = users.length - activeUsers;

        return {
            totalUsers: users.length,
            activeUsers,
            inactiveUsers,
            totalCitiesOnboarded: cities.filter((city) => city.onboarded).length,
            activeCitiesT0: cities.filter((city) => city.onboarded && city.t0Active).length,
            activeCitiesT3: cities.filter((city) => city.onboarded && city.t3Active).length,
            activeCitiesT6: cities.filter((city) => city.onboarded && city.t6Active).length
        };
    }
}

function createEmptyPermission(cityId: string): AdminUserCityAccessPayload {
    return {
        cityId,
        allAccess: false,
        t0Access: false,
        t3Access: false,
        t6Access: false,
        dataUploadAccess: false
    };
}